/**
 * @fileoverview Wallet Context Provider
 * Manages wallet connection state across the application
 */

import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import walletService, { WalletConnection, WalletAddress } from '../services/walletService'

interface WalletContextType {
	connection: WalletConnection
	isConnecting: boolean
	error: string | null
	connect: () => Promise<void>
	disconnect: () => Promise<void>
	selectAddress: (address: string) => void
	refreshBalances: () => Promise<void>
	getSelectedAddress: () => WalletAddress | undefined
}

const WalletContext = createContext<WalletContextType | undefined>(undefined)

interface WalletProviderProps {
	children: ReactNode
}

export const WalletProvider: React.FC<WalletProviderProps> = ({ children }) => {
	const [connection, setConnection] = useState<WalletConnection>({
		connected: false,
		addresses: [],
		totalBalance: 0
	})
	const [isConnecting, setIsConnecting] = useState(false)
	const [error, setError] = useState<string | null>(null)

	// Restore connection on app load
	useEffect(() => {
		const restoreConnection = async () => {
			try {
				const restored = await walletService.restoreConnection()
				setConnection(restored)
			} catch (err) {
				console.error('Failed to restore wallet connection:', err)
			}
		}

		restoreConnection()
	}, [])

	// Auto-refresh balances every 30 seconds when connected
	useEffect(() => {
		if (!connection.connected) return

		const interval = setInterval(async () => {
			await refreshBalances()
		}, 30000)

		return () => clearInterval(interval)
	}, [connection.connected])

	const connect = async (): Promise<void> => {
		setIsConnecting(true)
		setError(null)

		try {
			const newConnection = await walletService.connect()
			setConnection(newConnection)
		} catch (err) {
			const errorMessage = err instanceof Error ? err.message : 'Failed to connect wallet'
			setError(errorMessage)
			console.error('Wallet connection failed:', err)
		} finally {
			setIsConnecting(false)
		}
	}

	const disconnect = async (): Promise<void> => {
		try {
			await walletService.disconnect()
			setConnection({
				connected: false,
				addresses: [],
				totalBalance: 0
			})
			setError(null)
		} catch (err) {
			console.error('Failed to disconnect wallet:', err)
		}
	}

	const selectAddress = (address: string): void => {
		walletService.selectAddress(address)
		// Update local state to reflect the change
		setConnection(prev => ({
			...prev,
			selectedAddress: address
		}))
	}

	const refreshBalances = async (): Promise<void> => {
		if (!connection.connected) return

		try {
			await walletService.refreshBalances()
			const updatedConnection = walletService.getConnection()
			setConnection(updatedConnection)
		} catch (err) {
			console.error('Failed to refresh balances:', err)
		}
	}

	const getSelectedAddress = (): WalletAddress | undefined => {
		return walletService.getSelectedAddress()
	}

	const value: WalletContextType = {
		connection,
		isConnecting,
		error,
		connect,
		disconnect,
		selectAddress,
		refreshBalances,
		getSelectedAddress
	}

	return (
		<WalletContext.Provider value={value}>
			{children}
		</WalletContext.Provider>
	)
}

// Custom hook to use wallet context
export const useWallet = (): WalletContextType => {
	const context = useContext(WalletContext)
	if (!context) {
		throw new Error('useWallet must be used within a WalletProvider')
	}
	return context
}

export default WalletContext 